import { useMemo, type ReactNode } from "react";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  EmployeeCaseReportDay,
  EmployeeCaseReportRow,
  EmployeeCaseReportSummary,
} from "../../store/allocation/employeeDashboardSlice";
import { weekdayLabel } from "../../utils/operationalCalendar";
import { employeeRowBucket } from "../../utils/redesignBuckets";
import { employeeWorkSplit } from "./EmployeeCaseReportPanels";
import { ChartCard } from "./EmployeeCharts";
import { DASHBOARD } from "./dashboardTheme";

const CLEAN = DASHBOARD.teal;
const DESIGNED = DASHBOARD.header;
const EXTERNAL = DASHBOARD.terracotta;
const BLUNDER = "#B42318";
const NORMAL = DASHBOARD.internalLive;
const INTERNAL = DASHBOARD.purple;
const GRID = DASHBOARD.grid;
const MUTED = DASHBOARD.textMuted;

type DayPoint = {
  date: string;
  label: string;
  cases: number;
  cameBack: number;
  blunder: number;
  normal: number;
  liveUnits: number;
  redesignUnits: number;
  totalUnits: number;
  pct: number;
};

const boxUnits = (marked: number, unmarked: number) => marked + unmarked;

const dayFill = (color: string, date: string, selected: string | "all") =>
  selected === "all" || selected === date ? color : `${color}55`;

const formatPct = (value: number | null | undefined) =>
  value == null ? "—" : `${Number.isInteger(value) ? value : value.toFixed(1)}%`;

function shortDayLabel(iso: string, compact: boolean) {
  const day = iso.slice(8);
  if (compact) return day;
  return `${weekdayLabel(iso)} ${Number(day)}`;
}

function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color?: string }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-[#E8E8E4] bg-white px-3 py-2 text-xs shadow-lg">
      <p className="mb-1.5 font-semibold text-[#2C2C2C]">{label}</p>
      {payload.map((item) => (
        <div key={item.name} className="flex items-center justify-between gap-4 py-0.5">
          <span className="flex items-center gap-1.5 text-[#6B6B66]">
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: item.color }}
            />
            {item.name}
          </span>
          <span className="font-semibold tabular-nums text-[#2C2C2C]">
            {item.name.includes("%") ? formatPct(item.value) : item.value}
          </span>
        </div>
      ))}
    </div>
  );
}

function EmptyChart({ label = "No data in this period" }: { label?: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm" style={{ color: MUTED }}>
      {label}
    </div>
  );
}

const casesWord = (count: number) => `${count} case${count === 1 ? "" : "s"}`;

const shareOf = (part: number, whole: number) =>
  whole > 0 ? Math.round((part / whole) * 1000) / 10 : null;

function InsightCard({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="min-w-0 rounded-2xl border border-[#E8E8E4] bg-white p-5 shadow-sm">
      <h3
        className="text-[11px] font-semibold uppercase tracking-[0.12em]"
        style={{ color: MUTED }}
      >
        {title}
      </h3>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function CountMeter({
  label,
  detail,
  value,
  total,
  color,
}: {
  label: string;
  detail: string;
  value: number;
  total: number;
  color: string;
}) {
  const pct = shareOf(value, total) ?? 0;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[#2C2C2C]">{label}</p>
          <p className="text-[11px] leading-snug text-[#6B6B66]">{detail}</p>
        </div>
        <p className="shrink-0 text-right">
          <span className="text-lg font-bold tabular-nums text-[#2C2C2C]">{value}</span>
          <span className="ml-1.5 text-[11px] font-medium tabular-nums text-[#8A8A85]">
            {formatPct(shareOf(value, total))}
          </span>
        </p>
      </div>
      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-[#F0F0EC]">
        <div
          className="h-full rounded-full"
          style={{ width: `${Math.max(value > 0 ? 4 : 0, Math.min(100, pct))}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

export function OverviewCharts({
  days,
  rows,
  empName,
  summary,
  selectedDate,
  onSelectDate,
}: {
  days: EmployeeCaseReportDay[];
  rows: EmployeeCaseReportRow[];
  empName?: string | null;
  summary: EmployeeCaseReportSummary;
  selectedDate: string | "all";
  onSelectDate: (date: string | "all") => void;
}) {
  const compact = days.length > 16;
  const points: DayPoint[] = useMemo(() => {
    const byDay = new Map<string, EmployeeCaseReportRow[]>();
    for (const row of rows) {
      const date = row.operational_date;
      if (!date) continue;
      const list = byDay.get(date);
      if (list) list.push(row);
      else byDay.set(date, [row]);
    }
    return days.map((day) => {
      const dayRows = byDay.get(day.date) || [];
      const returns = dayRows.filter((row) => row.kind === "live" && row.came_back);
      const blunder = returns.filter(
        (row) => employeeRowBucket(row) === "blunder",
      ).length;
      const cameBack = returns.length;
      const split = employeeWorkSplit(dayRows, empName);
      const liveUnits =
        boxUnits(split.liveDesigned.marked, split.liveDesigned.unmarked) +
        boxUnits(split.liveQc.marked, split.liveQc.unmarked);
      const redesignUnits =
        boxUnits(split.redesignDesigned.marked, split.redesignDesigned.unmarked) +
        boxUnits(split.redesignQc.marked, split.redesignQc.unmarked);
      return {
        date: day.date,
        label: shortDayLabel(day.date, compact),
        cases: day.tracked_cases ?? day.designed_cases,
        cameBack,
        blunder,
        normal: Math.max(0, cameBack - blunder),
        liveUnits,
        redesignUnits,
        totalUnits: liveUnits + redesignUnits,
        pct: day.redesign_pct ?? 0,
      };
    });
  }, [days, rows, empName, compact]);

  const outcome = useMemo(() => {
    const tracked = summary.tracked_cases ?? summary.designed_cases;
    const cameBack = summary.came_back;
    const blunder = Math.min(
      cameBack,
      points.reduce((sum, point) => sum + point.blunder, 0),
    );
    const normal = Math.max(0, cameBack - blunder);
    const clean = Math.max(0, tracked - cameBack);
    const reasons = [...summary.categories].sort((a, b) => b.count - a.count);
    const peak = points.reduce<DayPoint | null>((best, point) => {
      if (point.cases <= 0) return best;
      if (!best || point.cameBack > best.cameBack) return point;
      if (point.cameBack === best.cameBack && point.pct > best.pct) return point;
      return best;
    }, null);
    return { tracked, cameBack, blunder, normal, clean, reasons, peak };
  }, [summary, points]);

  const hasVolume = points.some(
    (p) => p.cases > 0 || p.cameBack > 0 || p.totalUnits > 0,
  );
  const maxCases = Math.max(
    ...points.map((p) => Math.max(p.cameBack, p.blunder, p.normal)),
    1,
  );
  const caseMax = Math.max(4, Math.ceil(maxCases / 2) * 2);
  const maxUnits = Math.max(...points.map((p) => p.totalUnits), 1);
  const unitMax = Math.max(4, Math.ceil(maxUnits / 2) * 2);
  const tickInterval = days.length > 21 ? Math.ceil(days.length / 12) - 1 : 0;

  return (
    <div className="flex flex-col gap-3 min-w-0">
      <ChartCard
        title="Daily came back and units"
        height="h-80"
        className="border border-[#E8E8E4]"
      >
        {hasVolume ? (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={points}
              margin={{ top: 8, right: 8, left: -12, bottom: 0 }}
              barGap={2}
              onClick={(state) => {
                const date = (state?.activePayload?.[0]?.payload as DayPoint | undefined)
                  ?.date;
                if (!date) return;
                onSelectDate(selectedDate === date ? "all" : date);
              }}
            >
              <CartesianGrid stroke={GRID} vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: MUTED }}
                axisLine={false}
                tickLine={false}
                interval={tickInterval}
              />
              <YAxis
                yAxisId="cases"
                tick={{ fontSize: 11, fill: MUTED }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
                domain={[0, caseMax]}
                width={32}
              />
              <YAxis
                yAxisId="units"
                orientation="right"
                tick={{ fontSize: 11, fill: MUTED }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
                domain={[0, unitMax]}
                width={36}
              />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11, color: MUTED }} />
              <Bar
                yAxisId="cases"
                dataKey="cameBack"
                name="Came back"
                radius={[4, 4, 0, 0]}
                maxBarSize={16}
              >
                {points.map((point) => (
                  <Cell
                    key={`c-${point.date}`}
                    fill={dayFill(EXTERNAL, point.date, selectedDate)}
                    cursor="pointer"
                  />
                ))}
              </Bar>
              <Bar
                yAxisId="cases"
                dataKey="blunder"
                name="Blunder"
                radius={[4, 4, 0, 0]}
                maxBarSize={16}
              >
                {points.map((point) => (
                  <Cell
                    key={`b-${point.date}`}
                    fill={dayFill(BLUNDER, point.date, selectedDate)}
                    cursor="pointer"
                  />
                ))}
              </Bar>
              <Bar
                yAxisId="cases"
                dataKey="normal"
                name="Normal"
                radius={[4, 4, 0, 0]}
                maxBarSize={16}
              >
                {points.map((point) => (
                  <Cell
                    key={`n-${point.date}`}
                    fill={dayFill(NORMAL, point.date, selectedDate)}
                    cursor="pointer"
                  />
                ))}
              </Bar>
              <Line
                yAxisId="units"
                type="monotone"
                dataKey="totalUnits"
                name="Total units"
                stroke={DESIGNED}
                strokeWidth={2.5}
                dot={{ r: 2.5, fill: DESIGNED }}
                activeDot={{ r: 4 }}
              />
              <Line
                yAxisId="units"
                type="monotone"
                dataKey="liveUnits"
                name="Live units"
                stroke={CLEAN}
                strokeWidth={2}
                dot={{ r: 2.5, fill: CLEAN }}
                activeDot={{ r: 4 }}
              />
              <Line
                yAxisId="units"
                type="monotone"
                dataKey="redesignUnits"
                name="Redesign units"
                stroke={DASHBOARD.designUnitsCard}
                strokeWidth={2}
                dot={{ r: 2.5, fill: DASHBOARD.designUnitsCard }}
                activeDot={{ r: 4 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <EmptyChart />
        )}
      </ChartCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 [&>*]:min-w-0">
        <InsightCard title="Did cases come back?">
          {outcome.tracked <= 0 ? (
            <p className="text-sm text-[#6B6B66]">No designed or QC'd cases in this period.</p>
          ) : (
            <>
              <p className="text-base font-semibold leading-snug text-[#2C2C2C]">
                {outcome.cameBack === 0
                  ? `All ${casesWord(outcome.tracked)} stayed clean.`
                  : `${casesWord(outcome.cameBack)} of ${outcome.tracked} came back (${formatPct(shareOf(outcome.cameBack, outcome.tracked))}).`}
              </p>
              <p className="mt-1 text-[11px] leading-snug text-[#6B6B66]">
                Came back means the case was sent for a redesign. This includes cases the person designed and cases they QC'd.
              </p>
              <div className="mt-3 flex h-3 overflow-hidden rounded-full bg-[#F0F0EC]">
                {outcome.clean > 0 ? (
                  <div
                    style={{ width: `${(outcome.clean / outcome.tracked) * 100}%`, backgroundColor: CLEAN }}
                    title={`Stayed clean: ${outcome.clean}`}
                  />
                ) : null}
                {outcome.normal > 0 ? (
                  <div
                    style={{ width: `${(outcome.normal / outcome.tracked) * 100}%`, backgroundColor: EXTERNAL }}
                    title={`Came back, not a blunder: ${outcome.normal}`}
                  />
                ) : null}
                {outcome.blunder > 0 ? (
                  <div
                    style={{ width: `${(outcome.blunder / outcome.tracked) * 100}%`, backgroundColor: BLUNDER }}
                    title={`Blunder: ${outcome.blunder}`}
                  />
                ) : null}
              </div>
              <div className="mt-4 flex flex-col gap-3">
                <CountMeter
                  label="Stayed clean"
                  detail="Not sent back for a redesign"
                  value={outcome.clean}
                  total={outcome.tracked}
                  color={CLEAN}
                />
                <CountMeter
                  label="Came back, not a blunder"
                  detail="Sent back for another reason"
                  value={outcome.normal}
                  total={outcome.tracked}
                  color={EXTERNAL}
                />
                <CountMeter
                  label="Blunder"
                  detail="Sent back because of a mistake"
                  value={outcome.blunder}
                  total={outcome.tracked}
                  color={BLUNDER}
                />
              </div>
              <p className="mt-3 text-[11px] leading-snug text-[#6B6B66]">
                {summary.came_back_external} sent back by the client
                {" · "}
                {summary.came_back_internal} raised inside the lab
              </p>
            </>
          )}
        </InsightCard>

        <InsightCard title="Why they came back">
          {outcome.cameBack <= 0 ? (
            <p className="text-sm text-[#6B6B66]">
              No cases came back, so there are no reasons to show.
            </p>
          ) : !outcome.reasons.length ? (
            <p className="text-sm text-[#6B6B66]">
              {casesWord(outcome.cameBack)} came back, but no reason has been marked yet.
            </p>
          ) : (
            <>
              <p className="text-base font-semibold leading-snug text-[#2C2C2C]">
                {outcome.reasons.length} reason{outcome.reasons.length === 1 ? "" : "s"} on{" "}
                {casesWord(outcome.cameBack)} that came back.
              </p>
              <p className="mt-1 text-[11px] leading-snug text-[#6B6B66]">
                External means the client sent it back. Internal means our team raised it. One case can have more than one reason, so these numbers can add up to more than the cases.
              </p>
              <div className="mt-4 flex max-h-72 flex-col gap-3 overflow-y-auto pr-1">
                {outcome.reasons.map((reason) => {
                  const internal = reason.kind === "internal";
                  const top = outcome.reasons[0]?.count || 1;
                  return (
                    <div key={`${reason.kind}-${reason.category}`}>
                      <div className="flex items-baseline justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-[#2C2C2C]">
                            {reason.category || "No reason written"}
                          </p>
                          <p className="text-[11px] text-[#6B6B66]">
                            {internal ? "Internal · raised by our team" : "External · sent back by the client"}
                          </p>
                        </div>
                        <p className="shrink-0 text-right">
                          <span className="text-lg font-bold tabular-nums text-[#2C2C2C]">
                            {reason.count}
                          </span>
                          <span className="ml-1.5 text-[11px] font-medium tabular-nums text-[#8A8A85]">
                            {formatPct(shareOf(reason.count, outcome.cameBack))} of returns
                          </span>
                        </p>
                      </div>
                      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-[#F0F0EC]">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${Math.max(8, (reason.count / top) * 100)}%`,
                            backgroundColor: internal ? INTERNAL : EXTERNAL,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </InsightCard>
      </div>

      <InsightCard title="Came back each day">
        {outcome.tracked <= 0 ? (
          <p className="text-sm text-[#6B6B66]">No designed or QC'd cases in this period.</p>
        ) : (
          <>
            <p className="text-sm leading-snug text-[#2C2C2C]">
              {outcome.cameBack === 0
                ? `None of the ${casesWord(outcome.tracked)} came back on these days.`
                : outcome.peak && outcome.peak.cameBack > 0
                  ? `${casesWord(outcome.cameBack)} came back in total. The busiest day was ${outcome.peak.label}: ${outcome.peak.cameBack} of ${outcome.peak.cases} (${formatPct(outcome.peak.pct)})${outcome.peak.blunder > 0 ? `, including ${casesWord(outcome.peak.blunder)} marked as a blunder` : ""}.`
                  : `${casesWord(outcome.cameBack)} came back in total.`}
            </p>
            <p className="mt-1 text-[11px] leading-snug text-[#6B6B66]">
              Each column is one day. 2/4 means 2 of the 4 cases that day came back. Orange is a return that is not a blunder. Red includes a blunder. Click a day to filter the case list.
            </p>
            <div className="mt-4 overflow-x-auto">
              <div className="flex items-end gap-1.5 pb-1">
                {points.map((point) => {
                  const selected = selectedDate === "all" || selectedDate === point.date;
                  const barColor =
                    point.cases <= 0
                      ? "#E8E8E4"
                      : point.blunder > 0
                        ? BLUNDER
                        : point.cameBack > 0
                          ? EXTERNAL
                          : CLEAN;
                  const height = point.cases <= 0 ? 4 : Math.max(8, (point.pct / 100) * 88);
                  return (
                    <button
                      key={point.date}
                      type="button"
                      onClick={() => onSelectDate(selectedDate === point.date ? "all" : point.date)}
                      className={`flex w-14 shrink-0 flex-col items-center rounded-lg px-1 py-1 ${
                        selectedDate === point.date ? "bg-[#F3F7F6]" : "hover:bg-[#FAFAF8]"
                      } ${selected ? "" : "opacity-40"}`}
                      title={
                        point.cases <= 0
                          ? `${point.label}: no cases`
                          : `${point.label}: ${point.cameBack} of ${point.cases} came back (${formatPct(point.pct)}). ${point.blunder} blunder, ${point.normal} not a blunder.`
                      }
                    >
                      <span className="text-[11px] font-semibold tabular-nums text-[#2C2C2C]">
                        {point.cases > 0 ? formatPct(point.pct) : "—"}
                      </span>
                      <span className="mt-1 flex h-24 items-end">
                        <span
                          className="block w-7 rounded-md"
                          style={{ height, backgroundColor: barColor }}
                        />
                      </span>
                      <span className="mt-1 text-center text-[10px] leading-tight text-[#4A4A48]">
                        {point.label}
                      </span>
                      <span className="text-[10px] font-medium tabular-nums text-[#6B6B66]">
                        {point.cases > 0 ? `${point.cameBack}/${point.cases}` : "0"}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </InsightCard>
    </div>
  );
}

export function OverviewMixBar({
  summary,
  blunderCount = 0,
  blunderPct = null,
}: {
  summary: EmployeeCaseReportSummary;
  blunderCount?: number;
  blunderPct?: number | null;
}) {
  const total = (summary.tracked_cases ?? summary.designed_cases) || 1;
  const clean = Math.max(
    0,
    (summary.tracked_cases ?? summary.designed_cases) - summary.came_back,
  );
  const blunders = Math.min(Math.max(0, blunderCount), summary.came_back);
  const normalBack = Math.max(0, summary.came_back - blunders);
  const segments = [
    { label: "Clean", value: clean, color: CLEAN },
    { label: "Normal", value: normalBack, color: EXTERNAL },
    { label: "Blunder", value: blunders, color: "#B42318" },
  ];
  return (
    <div className="rounded-2xl border border-[#E8E8E4] bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8A8A85]">
          Period mix
        </p>
        <p className="text-sm font-semibold text-[#2C2C2C]">
          {formatPct(summary.redesign_pct)} all redesign · {formatPct(blunderPct)} blunder
        </p>
      </div>
      <div className="mt-3 flex h-3 overflow-hidden rounded-full bg-[#F0F0EC]">
        {segments.map((seg) =>
          seg.value > 0 ? (
            <div
              key={seg.label}
              title={`${seg.label}: ${seg.value}`}
              style={{
                width: `${Math.max(2, (seg.value / total) * 100)}%`,
                backgroundColor: seg.color,
              }}
            />
          ) : null,
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-4 text-xs text-[#6B6B66]">
        {segments.map((seg) => (
          <span key={seg.label} className="inline-flex items-center gap-1.5">
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: seg.color }}
            />
            {seg.label} · {seg.value}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: EXTERNAL }} />
          External · {summary.came_back_external}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: INTERNAL }} />
          Internal · {summary.came_back_internal}
        </span>
      </div>
    </div>
  );
}
